import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../services/api';
import axios from 'axios';
import { toast } from 'react-toastify';
import {
  FaUser, FaCar, FaTruckMoving, FaMapMarkerAlt,
  FaCalendarAlt, FaDollarSign, FaClipboardList,
  FaTimes, FaSpinner, FaHardHat, FaSearchLocation, FaRoute,
  FaCalculator, FaCheckCircle, FaPlus, FaTools, FaArrowLeft,
  FaCoins, FaInfoCircle, FaWrench,
} from 'react-icons/fa';
import CalculadoraPreco from './CalculadoraPreco';
import './CadastroOrdens.css';

// ─────────────────────────────────────────────────────────────────────────────
// MODAL DE CADASTRO RÁPIDO DE CLIENTE
// ─────────────────────────────────────────────────────────────────────────────
const QC_CSS = `
@keyframes qc-in {
  from { opacity:0; transform: scale(0.94) translateY(-14px); }
  to   { opacity:1; transform: scale(1)    translateY(0); }
}
@keyframes qc-overlay-in {
  from { opacity:0; }
  to   { opacity:1; }
}
.qc-input {
  width:100%; padding:10px 14px; border-radius:9px;
  border: 1.5px solid #e2e8f0; font-size:0.875rem;
  outline:none; color:#0f172a; background:#f8fafc;
  transition: border-color .15s, box-shadow .15s, background .15s;
  font-family: inherit;
}
.qc-input:focus {
  border-color: #3b82f6;
  background: #fff;
  box-shadow: 0 0 0 3px rgba(59,130,246,0.1);
}
.qc-input::placeholder { color: #94a3b8; }
.qc-label {
  display:block; font-size:0.68rem; font-weight:700;
  text-transform:uppercase; letter-spacing:0.06em;
  color:#475569; margin-bottom:5px;
}
.qc-required { color:#ef4444; margin-left:2px; }
.qc-save-btn {
  display:flex; align-items:center; gap:8px;
  padding:10px 22px; border-radius:10px; border:none;
  background:linear-gradient(135deg,#1d4ed8,#2563eb);
  color:#fff; font-size:0.84rem; font-weight:700;
  cursor:pointer; box-shadow:0 4px 16px rgba(37,99,235,0.32);
  transition: all .15s;
}
.qc-save-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow:0 8px 24px rgba(37,99,235,0.42);
}
.qc-save-btn:disabled { opacity:.65; cursor:not-allowed; }
.qc-cancel-btn {
  padding:10px 20px; border-radius:10px;
  border:1.5px solid #e2e8f0; background:transparent;
  color:#64748b; font-size:0.84rem; font-weight:600;
  cursor:pointer; transition:all .15s;
}
.qc-cancel-btn:hover { background:#f8fafc; border-color:#cbd5e1; color:#334155; }
`;

// ─────────────────────────────────────────────────────────────────────────────
// CSS COMPLEMENTAR DO TOOLTIP — mantido aqui para não exigir alteração no CSS
// ─────────────────────────────────────────────────────────────────────────────
const NOVAOS_TOOLTIP_CSS = `
.novaos-label-with-help { display: flex; align-items: center; gap: 6px; flex-wrap: wrap; }
.novaos-help-tooltip { position: relative; display: inline-flex; align-items: center; justify-content: center; color: #64748b; cursor: help; line-height: 1; }
.novaos-help-tooltip:hover { color: #2563eb; }
.novaos-help-tooltip-text {
  position: absolute; left: 50%; bottom: calc(100% + 9px);
  transform: translateX(-50%) translateY(4px);
  width: 285px; padding: 10px 12px; border-radius: 10px;
  background: #0f172a; color: #fff; font-size: 0.72rem; font-weight: 500;
  line-height: 1.45; text-transform: none; letter-spacing: normal;
  box-shadow: 0 12px 32px rgba(15, 23, 42, 0.24);
  opacity: 0; visibility: hidden; pointer-events: none; z-index: 9999;
  transition: opacity 0.15s ease, transform 0.15s ease, visibility 0.15s ease;
}
.novaos-help-tooltip-text::after {
  content: ""; position: absolute; top: 100%; left: 50%; transform: translateX(-50%);
  border-width: 6px; border-style: solid; border-color: #0f172a transparent transparent transparent;
}
.novaos-help-tooltip:hover .novaos-help-tooltip-text {
  opacity: 1; visibility: visible; transform: translateX(-50%) translateY(0);
}
`;

function CadastroRapidoClienteModal({ isOpen, onClose, onSuccess }) {
  const [dados, setDados] = useState({ nome:'', telefone:'', email:'', endereco:'', cpf_cnpj:'' });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen) setDados({ nome:'', telefone:'', email:'', endereco:'', cpf_cnpj:'' });
  }, [isOpen]);

  if (!isOpen) return null;

  const set = field => e => setDados(p => ({ ...p, [field]: e.target.value }));

  const handleSubmit = async e => {
    e.preventDefault();

    if (!dados.nome.trim()) {
      toast.error('O nome do cliente é obrigatório.');
      return;
    }

    setLoading(true);

    try {
      const { data } = await api.post('/api/clients/', {
        nome:     dados.nome.trim(),
        telefone: dados.telefone.trim() || undefined,
        email:    dados.email.trim()    || undefined,
        endereco: dados.endereco.trim() || undefined,
        cpf_cnpj: dados.cpf_cnpj.trim() || undefined,
      });

      toast.success(`✅ Cliente "${dados.nome}" cadastrado com sucesso!`);

      onSuccess({
        id: data.id || data.data?.id,
        nome: data.nome || data.data?.nome || dados.nome,
      });

      onClose();
    } catch (err) {
      toast.error(`Erro ao cadastrar: ${err.response?.data?.error || err.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <style>{QC_CSS}</style>

      <div
        style={{
          position:'fixed', inset:0, zIndex:100001,
          background:'rgba(2,6,23,0.62)', display:'flex',
          alignItems:'center', justifyContent:'center', padding:16,
          backdropFilter:'blur(6px)', animation:'qc-overlay-in .18s ease-out',
        }}
        onClick={e => e.target === e.currentTarget && onClose()}
      >
        <div
          style={{
            background:'#fff', borderRadius:22, width:'min(500px, 96vw)',
            boxShadow:'0 32px 80px rgba(0,0,0,0.2), 0 0 0 1px rgba(0,0,0,0.04)',
            animation:'qc-in .22s cubic-bezier(0.16,1,0.3,1) both', overflow:'hidden',
          }}
        >
          <div style={{ height:4, background:'linear-gradient(90deg, #2563eb, #06b6d4, #10b981)' }} />

          <div style={{ padding:'26px 28px 24px' }}>
            <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', marginBottom:24 }}>
              <div style={{ display:'flex', alignItems:'center', gap:13 }}>
                <div
                  style={{
                    width:44, height:44, borderRadius:12,
                    background:'linear-gradient(135deg,#eff6ff,#dbeafe)',
                    border:'1px solid #bfdbfe', display:'flex',
                    alignItems:'center', justifyContent:'center',
                    fontSize:20, flexShrink:0,
                  }}
                >
                  ⚡
                </div>
                <div>
                  <div style={{ fontWeight:800, fontSize:'1.02rem', color:'#0f172a', lineHeight:1 }}>
                    Cadastro Rápido
                  </div>
                  <div style={{ fontSize:'0.72rem', color:'#64748b', marginTop:3 }}>
                    Novo cliente diretamente na base
                  </div>
                </div>
              </div>

              <button
                type="button"
                onClick={onClose}
                style={{
                  width:32, height:32, borderRadius:8,
                  border:'1.5px solid #e2e8f0', background:'#f8fafc',
                  cursor:'pointer', display:'flex', alignItems:'center',
                  justifyContent:'center', color:'#94a3b8', transition:'all .12s', flexShrink:0,
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.background='#fee2e2';
                  e.currentTarget.style.borderColor='#fca5a5';
                  e.currentTarget.style.color='#ef4444';
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.background='#f8fafc';
                  e.currentTarget.style.borderColor='#e2e8f0';
                  e.currentTarget.style.color='#94a3b8';
                }}
              >
                <FaTimes size={12} />
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div style={{ marginBottom:14 }}>
                <label className="qc-label">
                  Nome Completo <span className="qc-required">*</span>
                </label>
                <input
                  className="qc-input" type="text" value={dados.nome}
                  onChange={set('nome')} required placeholder="Ex: João da Silva" autoFocus
                />
              </div>

              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:14 }}>
                <div>
                  <label className="qc-label">Telefone</label>
                  <input
                    className="qc-input" type="tel" value={dados.telefone}
                    onChange={set('telefone')} placeholder="(11) 99999-9999"
                  />
                </div>

                <div>
                  <label className="qc-label">E-mail</label>
                  <input
                    className="qc-input" type="email" value={dados.email}
                    onChange={set('email')} placeholder="email@exemplo.com"
                  />
                </div>
              </div>

              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:24 }}>
                <div>
                  <label className="qc-label">Endereço</label>
                  <input
                    className="qc-input" type="text" value={dados.endereco}
                    onChange={set('endereco')} placeholder="Rua, número, bairro"
                  />
                </div>

                <div>
                  <label className="qc-label">CPF / CNPJ</label>
                  <input
                    className="qc-input" type="text" value={dados.cpf_cnpj}
                    onChange={set('cpf_cnpj')} placeholder="000.000.000-00"
                  />
                </div>
              </div>

              <div style={{ display:'flex', gap:10, justifyContent:'flex-end', paddingTop:4, borderTop:'1px solid #f1f5f9' }}>
                <button type="button" className="qc-cancel-btn" onClick={onClose}>
                  Cancelar
                </button>

                <button type="submit" className="qc-save-btn" disabled={loading}>
                  {loading ? (
                    <>
                      <FaSpinner style={{ animation:'spin 1s linear infinite' }} />
                      Salvando...
                    </>
                  ) : (
                    <>
                      <FaPlus size={11} />
                      Salvar Cliente
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// MODAL PRINCIPAL — NOVA ORDEM DE SERVIÇO
// ─────────────────────────────────────────────────────────────────────────────
export default function CadastroOrdensModal({ isOpen, onClose, onSuccess, user }) {
  const perfil  = user?.perfil || user?.role;
  const isAdmin = perfil === 'admin' || perfil === 'admin_geral';
  const tenantId = user?.cliente_id || user?.tenant_id;

  // evita warning caso ainda não use em algum ambiente/build
  void tenantId;

  // ── Tipo de OS escolhido no sub-modal inicial: null | 'servico' | 'tarefa'
  const [tipoOS, setTipoOS] = useState(null);

  // ── Form state (OS de Serviço)
  const [formData, setFormData] = useState({
    cliente_id:           '',
    placa_cliente:        '',
    cep_destino:          '',
    modalidade:           'municipal',
    endereco_atendimento: '',
    motorista_id:         '',
    veiculo_guincho_id:   '',
    data_hora:            new Date().toISOString().slice(0, 16),
    valor:                '',
    descricao:            '',
  });

  // ── Form state (OS de Tarefa interna)
  //    [SETA-COND] endereco_atendimento é OPCIONAL. Em branco, o backend grava
  //    o rótulo "Tarefa interna" e o app não renderiza o botão de rota.
  const [formTarefa, setFormTarefa] = useState({
    categoria_despesa_id: '',
    endereco_atendimento: '',
    veiculo_id:           '',
    motorista_id:         '',
    data_hora:            new Date().toISOString().slice(0, 16),
    descricao:            '',
    custo:                '',
  });

  const [mostrarCusto, setMostrarCusto] = useState(false);
  // [SEM-VALOR] Marca a tarefa como sem custo: campo cinza + envia valor 0.
  const [semValor, setSemValor]         = useState(false);

  // ── Data lists
  const [clientes,          setClientes]          = useState([]);
  const [motoristas,        setMotoristas]        = useState([]);
  const [guinchos,          setGuinchos]          = useState([]);
  const [categoriasDespesa, setCategoriasDespesa] = useState([]);

  // ── UI state
  const [loading,         setLoading]         = useState(false);
  const [loadingData,     setLoadingData]     = useState(false);
  const [buscandoCep,     setBuscandoCep]     = useState(false);
  const [buscandoVeiculo, setBuscandoVeiculo] = useState(false);
  const [showCalculadora, setShowCalculadora] = useState(false);
  const [showQuickClient, setShowQuickClient] = useState(false);
  const [submitted,       setSubmitted]       = useState(false);

  // Refs para inputs com foco especial
  const cepRef   = useRef(null);
  const valorRef = useRef(null);

  // ── Carregar listas ao abrir + resetar estados
  useEffect(() => {
    if (!isOpen) return;

    setSubmitted(false);
    setTipoOS(null);
    setMostrarCusto(false);
    setSemValor(false); // [SEM-VALOR] reset ao reabrir o modal

    setFormData({
      cliente_id: '',
      placa_cliente: '',
      cep_destino: '',
      modalidade: 'municipal',
      endereco_atendimento: '',
      motorista_id: '',
      veiculo_guincho_id: '',
      data_hora: new Date().toISOString().slice(0, 16),
      valor: '',
      descricao: '',
    });

    setFormTarefa({
      categoria_despesa_id: '',
      endereco_atendimento: '',
      veiculo_id: '',
      motorista_id: '',
      data_hora: new Date().toISOString().slice(0, 16),
      descricao: '',
      custo: '',
    });

    carregarDados();
  }, [isOpen]);

  const carregarDados = async () => {
    setLoadingData(true);

    try {
      const [resClientes, resUsuarios, resVeiculos, resCategorias] = await Promise.all([
        api.get('/api/clients/', { params: { limit: 1000 } }),
        api.get('/api/usuarios/selecao'),
        api.get('/api/vehicles', { params: { limit: 1000 } }),
        api.get('/api/gastos/categorias'),
      ]);

      setClientes(resClientes.data?.data || resClientes.data || []);

      const listaUsuarios = resUsuarios.data?.data || resUsuarios.data || [];
      setMotoristas(listaUsuarios.filter(u => u.perfil === 'operacional'));

      const listaVeiculos = resVeiculos.data?.data || resVeiculos.data || [];
      setGuinchos(listaVeiculos.filter(v => v.status !== 'Vendido'));

      const listaCategorias = resCategorias.data?.data || resCategorias.data || [];
      setCategoriasDespesa(listaCategorias);
    } catch (err) {
      console.error('Erro ao carregar dados:', err);
      toast.error('Erro ao carregar dados do sistema.');
    } finally {
      setLoadingData(false);
    }
  };

  // ── Atualizar campo genérico (OS de Serviço)
  const setField = useCallback((field, value) => {
    setFormData(p => ({ ...p, [field]: value }));
  }, []);

  // ── Atualizar campo genérico (OS de Tarefa)
  const setFieldTarefa = useCallback((field, value) => {
    setFormTarefa(p => ({ ...p, [field]: value }));
  }, []);

  // ── Busca de CEP via ViaCEP
  const buscarCep = useCallback(async (cep) => {
    const clean = cep.replace(/\D/g, '');

    if (clean.length !== 8) return;

    setBuscandoCep(true);

    try {
      const { data } = await axios.get(`https://viacep.com.br/ws/${clean}/json/`);

      if (data.erro) {
        toast.warn('CEP não encontrado.');
        return;
      }

      const modalidade = 'municipal';

      setFormData(p => ({
        ...p,
        modalidade,
        endereco_atendimento: [data.logradouro, data.bairro, data.localidade, data.uf].filter(Boolean).join(', '),
      }));
    } catch {
      toast.error('Erro ao consultar o CEP.');
    } finally {
      setBuscandoCep(false);
    }
  }, []);

  // ── Ao selecionar motorista (OS de Serviço) → busca veículo atual
  const handleMotoristaChange = useCallback(async (motoristaId) => {
    setField('motorista_id', motoristaId);
    setField('veiculo_guincho_id', '');

    if (!motoristaId) return;

    setBuscandoVeiculo(true);

    try {
      const { data } = await api.get(`/api/usuarios/${motoristaId}/veiculo-atual`);

      if (data?.veiculo?.id) {
        setField('veiculo_guincho_id', String(data.veiculo.id));

        toast.info(
          `Comprovativo 1 🚛 ${data.veiculo.placa} — ${data.veiculo.marca} ${data.veiculo.modelo} (veículo atual do motorista)`,
          { autoClose: 3000 }
        );
      } else {
        toast.info('Motorista sem veículo ativo no App. Selecione manualmente.', { autoClose: 3000 });
      }
    } catch {
      toast.warn('Não foi possível buscar o veículo do motorista.');
    } finally {
      setBuscandoVeiculo(false);
    }
  }, [setField]);

  // ── Novo cliente criado via quick modal
  const handleNovoClienteCriado = useCallback(async ({ id, nome }) => {
    try {
      const res = await api.get('/api/clients/', { params:{ limit:500 } });
      setClientes(res.data?.data || res.data || []);
    } catch {}

    setField('cliente_id', String(id));
    toast.success(`🎉 "${nome}" selecionado automaticamente!`, { autoClose:2500 });
  }, [setField]);

  // ── Aplicar valor da calculadora
  const handleAplicarValor = useCallback((valor) => {
    setField('valor', valor);
    setShowCalculadora(false);

    if (valorRef.current) {
      valorRef.current.style.borderColor = '#10b981';

      setTimeout(() => {
        if (valorRef.current) valorRef.current.style.borderColor = '';
      }, 2000);
    }

    toast.success(
      `✅ Valor R$ ${Number(valor).toLocaleString('pt-BR', { minimumFractionDigits:2 })} aplicado!`,
      { autoClose:2000 }
    );
  }, [setField]);

  // ── Voltar para a tela de escolha Serviço × Tarefa
  const handleVoltarEscolha = useCallback(() => {
    setTipoOS(null);
  }, []);

  // ── Submit — OS de Serviço
  const handleSubmit = async e => {
    e.preventDefault();

    if (!formData.cliente_id) {
      toast.error('Selecione o cliente solicitante.');
      return;
    }

    if (!formData.placa_cliente.trim()) {
      toast.error('Informe a placa do veículo do cliente.');
      return;
    }

    if (!formData.endereco_atendimento.trim()) {
      toast.error('Informe o endereço de atendimento.');
      return;
    }

    // Motorista agora é opcional.
    // Se escolher motorista, aí sim exigimos o veículo guincho vinculado/selecionado.
    if (formData.motorista_id && !formData.veiculo_guincho_id) {
      toast.error('Ao selecionar um motorista, selecione também o veículo guincho.');
      return;
    }

    if (!formData.valor || isNaN(Number(formData.valor))) {
      toast.error('Informe o valor da OS.');
      return;
    }

    if (!formData.descricao.trim()) {
      toast.error('Descreva o problema / serviço.');
      return;
    }

    setLoading(true);

    try {
      const payload = {
        cliente_id:            formData.cliente_id,
        motorista_id:          formData.motorista_id || null,
        veiculo_id:            formData.veiculo_guincho_id || null,
        local_atendimento:     formData.endereco_atendimento.trim(),
        descricao:             formData.descricao.trim(),
        data_criacao:          formData.data_hora,
        valor:                 parseFloat(formData.valor),
        placa_veiculo:         formData.placa_cliente.trim().toUpperCase(),
        modalidade_transporte: formData.modalidade,
        tipo_os:               'servico',
        origem:                'interno',
      };

      const { data } = await api.post('/api/ordens', payload);

      setSubmitted(true);
      toast.success('✅ Ordem de Serviço criada com sucesso!');

      onSuccess && onSuccess(data);

      setTimeout(() => {
        setSubmitted(false);
        onClose();
      }, 1400);
    } catch (err) {
      toast.error(`Erro ao criar OS: ${err.response?.data?.error || err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // ── Submit — OS de Tarefa interna
  const handleSubmitTarefa = async e => {
    e.preventDefault();

    if (!formTarefa.categoria_despesa_id) {
      toast.error('Selecione o tipo da tarefa.');
      return;
    }

    if (!formTarefa.data_hora) {
      toast.error('Informe a data e hora da tarefa.');
      return;
    }

    if (!formTarefa.descricao.trim()) {
      toast.error('Descreva a tarefa a ser executada.');
      return;
    }

    // [SEM-VALOR] Só valida o custo quando o campo está aberto E não é sem valor.
    if (mostrarCusto && !semValor && formTarefa.custo !== '' && isNaN(Number(formTarefa.custo))) {
      toast.error('Custo estimado inválido.');
      return;
    }

    setLoading(true);

    try {
      const payload = {
        tipo_os:              'tarefa',
        categoria_despesa_id: formTarefa.categoria_despesa_id || null,
        veiculo_id:           formTarefa.veiculo_id || null,
        motorista_id:         formTarefa.motorista_id || null,
        // [SETA-COND] endereço opcional → backend usa "Tarefa interna" se vazio
        local_atendimento:    formTarefa.endereco_atendimento.trim() || null,
        descricao:            formTarefa.descricao.trim(),
        data_criacao:         formTarefa.data_hora,
        // [SEM-VALOR] sem valor OU custo oculto → 0; senão o custo estimado.
        valor:                (mostrarCusto && !semValor) ? (parseFloat(formTarefa.custo) || 0) : 0,
        origem:               'interno',
      };

      const { data } = await api.post('/api/ordens', payload);

      setSubmitted(true);
      toast.success('✅ Tarefa registrada com sucesso!');

      onSuccess && onSuccess(data);

      setTimeout(() => {
        setSubmitted(false);
        onClose();
      }, 1400);
    } catch (err) {
      toast.error(`Erro ao registrar tarefa: ${err.response?.data?.error || err.message}`);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const modalidadeOpts = [
    { value:'municipal',      label:'Municipal (NFS-e)' },
    { value:'intermunicipal', label:'Intermunicipal (CTE)' },
  ];

  // Categoria selecionada na tarefa — usada para destacar o vínculo com a frota
  const categoriaTarefaSelecionada = categoriasDespesa.find(
    c => String(c.id) === String(formTarefa.categoria_despesa_id)
  );

  const isManutencaoFrota = categoriaTarefaSelecionada?.nome === 'Manutenção de Frota';

  // ── Subtítulo dinâmico do header
  let subtitulo;

  if (tipoOS === 'tarefa') {
    subtitulo = (
      <>
        Atividade interna da frota para <strong>{user?.tenant_nome || user?.empresa || 'sua empresa'}</strong>
      </>
    );
  } else if (tipoOS === 'servico') {
    subtitulo = (
      <>
        Registro de chamados para <strong>{user?.tenant_nome || user?.empresa || 'sua empresa'}</strong>
      </>
    );
  } else {
    subtitulo = (
      <>
        O que você precisa registrar para <strong>{user?.tenant_nome || user?.empresa || 'sua empresa'}</strong>?
      </>
    );
  }

  return (
    <>
      <style>{NOVAOS_TOOLTIP_CSS}</style>

      {/* ─── CALCULADORA OVERLAY ──────────────────────────────────────────── */}
      {showCalculadora && (
        <CalculadoraPreco
          isAdmin={isAdmin}
          onClose={() => setShowCalculadora(false)}
          onAplicar={handleAplicarValor}
        />
      )}

      {/* ─── QUICK CLIENT MODAL ──────────────────────────────────────────── */}
      <CadastroRapidoClienteModal
        isOpen={showQuickClient}
        onClose={() => setShowQuickClient(false)}
        onSuccess={handleNovoClienteCriado}
      />

      {/* ─── MAIN MODAL OVERLAY ──────────────────────────────────────────── */}
      <div className="novaos-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
        <div className="novaos-modal">

          {/* ════ HEADER ══════════════════════════════════════════════════ */}
          <div className="novaos-header">
            <div className="novaos-header-info">
              {tipoOS !== null && (
                <button type="button" className="novaos-back-btn" onClick={handleVoltarEscolha}>
                  <FaArrowLeft size={11} /> Voltar
                </button>
              )}

              <h2 className="novaos-title">
                {tipoOS === 'tarefa' ? 'Nova Tarefa Interna' : 'Nova Ordem de Serviço'}
              </h2>

              <p className="novaos-subtitle">{subtitulo}</p>
            </div>

            <button className="novaos-close-btn" type="button" onClick={onClose} title="Fechar">
              <FaTimes size={14} />
            </button>
          </div>

          {/* ════ ESCOLHA: SERVIÇO × TAREFA ══════════════════════════════════ */}
          {tipoOS === null && (
            <div className="novaos-choice-wrap">
              <div className="novaos-choice-grid">

                <button
                  type="button"
                  className="novaos-choice-card novaos-choice-card--servico"
                  onClick={() => setTipoOS('servico')}
                >
                  <div className="novaos-choice-icon novaos-choice-icon--servico">
                    <FaTruckMoving />
                  </div>

                  <h3>OS de Serviço</h3>

                  <p>
                    Atendimento a cliente — guincho, reboque ou assistência no local.
                    Ao ser concluída, gera <strong>receita</strong> e é faturada para o cliente.
                  </p>

                  <span className="novaos-choice-tag novaos-choice-tag--servico">
                    <FaDollarSign size={10} /> Gera faturamento
                  </span>
                </button>

                <button
                  type="button"
                  className="novaos-choice-card novaos-choice-card--tarefa"
                  onClick={() => setTipoOS('tarefa')}
                >
                  <div className="novaos-choice-icon novaos-choice-icon--tarefa">
                    <FaTools />
                  </div>

                  <h3>OS de Tarefa</h3>

                  <p>
                    Atividade interna da frota — manutenção, troca de pneus, revisão,
                    abastecimento programado. Ao ser concluída, pode gerar uma <strong>despesa</strong>.
                  </p>

                  <span className="novaos-choice-tag novaos-choice-tag--tarefa">
                    <FaWrench size={10} /> Uso interno
                  </span>
                </button>

              </div>
            </div>
          )}

          {/* ════ BODY — FORMULÁRIOS ═════════════════════════════════════════ */}
          {tipoOS !== null && (
            loadingData ? (
              <div className="novaos-loading-state">
                <FaSpinner className="spin" size={22} />
                <span>Carregando dados…</span>
              </div>
            ) : tipoOS === 'servico' ? (

              // ─────────────────────────────────────────────────────────────
              // FORMULÁRIO — OS DE SERVIÇO
              // ─────────────────────────────────────────────────────────────
              <form className="novaos-form" onSubmit={handleSubmit} noValidate>
                <div className="novaos-grid-2">

                  {/* ──────── DADOS DO CLIENTE ──────────────────────────── */}
                  <div className="novaos-card">
                    <div className="novaos-card-title">
                      <FaUser className="icon-blue" />
                      Dados do Cliente
                    </div>

                    {/* Cliente Solicitante */}
                    <div className="novaos-input-group">
                      <label className="novaos-label">
                        Cliente Solicitante <span className="req">*</span>
                      </label>

                      <div className="novaos-row-with-btn">
                        <select
                          className="novaos-select"
                          value={formData.cliente_id}
                          onChange={e => setField('cliente_id', e.target.value)}
                          required
                        >
                          <option value="">Selecione um cliente…</option>
                          {clientes.map(c => (
                            <option key={c.id} value={String(c.id)}>
                              {c.nome}{c.telefone ? ` — ${c.telefone}` : ''}
                            </option>
                          ))}
                        </select>

                        <button
                          type="button"
                          className="novaos-inline-add-btn"
                          onClick={() => setShowQuickClient(true)}
                          title="Cadastrar novo cliente"
                        >
                          <FaPlus size={10} /> Novo
                        </button>
                      </div>
                    </div>

                    {/* Placa do Veículo */}
                    <div className="novaos-input-group">
                      <label className="novaos-label">
                        Placa do Veículo (Cliente) <span className="req">*</span>
                      </label>

                      <div className="novaos-input-icon">
                        <FaCar className="input-icon" />
                        <input
                          type="text"
                          className="novaos-input with-icon"
                          value={formData.placa_cliente}
                          onChange={e => setField('placa_cliente', e.target.value.toUpperCase())}
                          placeholder="AAA-0000"
                          maxLength={8}
                        />
                      </div>
                    </div>

                    {/* CEP + Modalidade */}
                    <div className="novaos-row-2">
                      <div className="novaos-input-group">
                        <label className="novaos-label">CEP Destino</label>

                        <div className="novaos-input-icon">
                          {buscandoCep ? (
                            <FaSpinner className="input-icon spin" />
                          ) : (
                            <FaSearchLocation className="input-icon" />
                          )}

                          <input
                            ref={cepRef}
                            type="text"
                            className="novaos-input with-icon"
                            value={formData.cep_destino}
                            onChange={e => {
                              const v = e.target.value.replace(/\D/g,'').slice(0,8);
                              setField('cep_destino', v);
                              if (v.length === 8) buscarCep(v);
                            }}
                            placeholder="Somente números"
                            maxLength={9}
                          />
                        </div>
                      </div>

                      <div className="novaos-input-group">
                        <label className="novaos-label">
                          Modalidade <span className="badge-auto">(Auto)</span>
                        </label>

                        <div className="novaos-input-icon">
                          <FaRoute className="input-icon" />

                          <select
                            className="novaos-select with-icon"
                            value={formData.modalidade}
                            onChange={e => setField('modalidade', e.target.value)}
                          >
                            {modalidadeOpts.map(o => (
                              <option key={o.value} value={o.value}>
                                {o.label}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>

                    {/* Endereço de Atendimento */}
                    <div className="novaos-input-group">
                      <label className="novaos-label">
                        Endereço de Atendimento <span className="req">*</span>
                      </label>

                      <div className="novaos-input-icon">
                        <FaMapMarkerAlt className="input-icon" />
                        <input
                          type="text"
                          className="novaos-input with-icon"
                          value={formData.endereco_atendimento}
                          onChange={e => setField('endereco_atendimento', e.target.value)}
                          placeholder="Onde o guincho deve ir?"
                        />
                      </div>
                    </div>
                  </div>

                  {/* ──────── LOGÍSTICA E OPERAÇÃO ─────────────────────── */}
                  <div className="novaos-card">
                    <div className="novaos-card-title">
                      <FaTruckMoving className="icon-orange" />
                      Logística e Operação
                    </div>

                    {/* Motorista */}
                    <div className="novaos-input-group">
                      <label className="novaos-label novaos-label-with-help">
                        Motorista Designado
                        <span className="badge-auto">opcional</span>

                        <span className="novaos-help-tooltip">
                          <FaInfoCircle size={11} />
                          <span className="novaos-help-tooltip-text">
                            Ao escolher um motorista, a OS será atribuída automaticamente a ele.
                            Se deixar em branco, a OS ficará na fila para distribuição automática.
                          </span>
                        </span>
                      </label>

                      <div className="novaos-input-icon">
                        <FaHardHat className="input-icon" />

                        <select
                          className="novaos-select with-icon"
                          value={formData.motorista_id}
                          onChange={e => handleMotoristaChange(e.target.value)}
                        >
                          <option value="">Deixar na fila / distribuição automática</option>
                          {motoristas.map(m => (
                            <option key={m.id} value={String(m.id)}>
                              {m.nome}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    {/* Veículo Guincho */}
                    <div className="novaos-input-group">
                      <label className="novaos-label novaos-label-with-help">
                        Veículo Guincho
                        {formData.motorista_id ? (
                          <span className="req">*</span>
                        ) : (
                          <span className="badge-auto">opcional</span>
                        )}

                        <span className="novaos-help-tooltip">
                          <FaInfoCircle size={11} />
                          <span className="novaos-help-tooltip-text">
                            O veículo só é obrigatório quando um motorista for escolhido.
                            Sem motorista, a OS permanece na fila para atribuição posterior.
                          </span>
                        </span>
                      </label>

                      <div className="novaos-input-icon">
                        {buscandoVeiculo ? (
                          <FaSpinner className="input-icon spin" />
                        ) : (
                          <FaTruckMoving className="input-icon" />
                        )}

                        <select
                          className="novaos-select with-icon"
                          value={formData.veiculo_guincho_id}
                          onChange={e => setField('veiculo_guincho_id', e.target.value)}
                        >
                          <option value="">
                            {formData.motorista_id
                              ? 'Selecione o guincho disponível…'
                              : 'Opcional — selecionar depois'}
                          </option>

                          {guinchos.map(g => (
                            <option key={g.id} value={String(g.id)}>
                              {g.placa ? `${g.placa} — ` : ''}{g.modelo || g.nome}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    {/* Data e Hora */}
                    <div className="novaos-input-group">
                      <label className="novaos-label">
                        Data e Hora <span className="req">*</span>
                      </label>

                      <div className="novaos-input-icon">
                        <FaCalendarAlt className="input-icon" />

                        <input
                          type="datetime-local"
                          className="novaos-input with-icon"
                          value={formData.data_hora}
                          onChange={e => setField('data_hora', e.target.value)}
                          required
                        />
                      </div>
                    </div>

                    {/* Valor */}
                    <div className="novaos-input-group">
                      <label className="novaos-label">
                        Valor (R$) <span className="req">*</span>
                      </label>

                      <div className="novaos-valor-row">
                        <div className="novaos-input-icon" style={{ flex:1 }}>
                          <FaDollarSign className="input-icon" />

                          <input
                            ref={valorRef}
                            type="number"
                            min="0"
                            step="0.01"
                            className="novaos-input with-icon"
                            value={formData.valor}
                            onChange={e => setField('valor', e.target.value)}
                            placeholder="0.00"
                            required
                          />
                        </div>

                        <button
                          type="button"
                          className="novaos-calc-btn"
                          onClick={() => setShowCalculadora(true)}
                          title="Abrir calculadora de preço"
                        >
                          <FaCalculator size={12} />
                          Calcular
                        </button>
                      </div>

                      {formData.valor && !isNaN(Number(formData.valor)) && Number(formData.valor) > 0 && (
                        <div className="novaos-valor-badge">
                          <FaCheckCircle size={10} />
                          R$ {Number(formData.valor).toLocaleString('pt-BR', { minimumFractionDigits:2 })} definido
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* ──────── DETALHES DO SERVIÇO ─────────────────────────── */}
                <div className="novaos-card" style={{ marginTop:16 }}>
                  <div className="novaos-card-title">
                    <FaClipboardList className="icon-blue" />
                    Detalhes do Serviço
                  </div>

                  <div className="novaos-input-group">
                    <label className="novaos-label">
                      Descrição do Problema / Serviço <span className="req">*</span>
                    </label>

                    <textarea
                      className="novaos-textarea"
                      rows={4}
                      value={formData.descricao}
                      onChange={e => setField('descricao', e.target.value)}
                      placeholder="Ex: Veículo não liga, chave perdida, pneu estourado na rodovia…"
                      required
                    />
                  </div>
                </div>

                {/* ════ FOOTER — AÇÕES ════════════════════════════════════ */}
                <div className="novaos-footer">
                  <button type="button" className="novaos-btn-cancel" onClick={onClose}>
                    Cancelar
                  </button>

                  <button type="submit" className="novaos-btn-confirm" disabled={loading || submitted}>
                    {loading ? (
                      <>
                        <FaSpinner style={{ animation:'spin 1s linear infinite' }} />
                        Salvando…
                      </>
                    ) : submitted ? (
                      <>
                        <FaCheckCircle />
                        OS Criada!
                      </>
                    ) : (
                      'Confirmar e Agendar'
                    )}
                  </button>
                </div>
              </form>

            ) : (

              // ─────────────────────────────────────────────────────────────
              // FORMULÁRIO — OS DE TAREFA INTERNA
              // ─────────────────────────────────────────────────────────────
              <form className="novaos-form" onSubmit={handleSubmitTarefa} noValidate>
                <div className="novaos-grid-2">

                  {/* ──────── DETALHES DA TAREFA ────────────────────────── */}
                  <div className="novaos-card">
                    <div className="novaos-card-title">
                      <FaTools className="icon-orange" />
                      Detalhes da Tarefa
                    </div>

                    {/* Tipo de Tarefa / Categoria de Despesa */}
                    <div className="novaos-input-group">
                      <label className="novaos-label">
                        Tipo de Tarefa <span className="req">*</span>
                      </label>

                      <div className="novaos-input-icon">
                        <FaClipboardList className="input-icon" />

                        <select
                          className="novaos-select with-icon"
                          value={formTarefa.categoria_despesa_id}
                          onChange={e => setFieldTarefa('categoria_despesa_id', e.target.value)}
                          required
                        >
                          <option value="">Selecione o tipo de tarefa…</option>
                          {categoriasDespesa.map(c => (
                            <option key={c.id} value={String(c.id)}>
                              {c.nome}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    {/* Veículo da Frota */}
                    <div className="novaos-input-group">
                      <label className="novaos-label">
                        Veículo da Frota
                        <span className="badge-auto">(opcional)</span>
                      </label>

                      <div className="novaos-input-icon">
                        <FaTruckMoving className="input-icon" />

                        <select
                          className="novaos-select with-icon"
                          value={formTarefa.veiculo_id}
                          onChange={e => setFieldTarefa('veiculo_id', e.target.value)}
                        >
                          <option value="">Sem veículo vinculado</option>
                          {guinchos.map(g => (
                            <option key={g.id} value={String(g.id)}>
                              {g.placa ? `${g.placa} — ` : ''}{g.modelo || g.nome}
                            </option>
                          ))}
                        </select>
                      </div>

                      {isManutencaoFrota && !formTarefa.veiculo_id && (
                        <div className="novaos-info-banner">
                          <FaInfoCircle size={12} />
                          Selecione o veículo para registrar esta manutenção no histórico da frota.
                        </div>
                      )}
                    </div>

                    {/* Endereço da Tarefa — OPCIONAL [SETA-COND] */}
                    <div className="novaos-input-group">
                      <label className="novaos-label novaos-label-with-help">
                        Endereço da Tarefa
                        <span className="badge-auto">(opcional)</span>

                        <span className="novaos-help-tooltip">
                          <FaInfoCircle size={11} />
                          <span className="novaos-help-tooltip-text">
                            Informe só se a tarefa tiver um local de deslocamento (borracharia,
                            oficina, etc). Em branco, o app mostra "Tarefa interna" e não exibe
                            o botão de rota para o motorista.
                          </span>
                        </span>
                      </label>

                      <div className="novaos-input-icon">
                        <FaMapMarkerAlt className="input-icon" />
                        <input
                          type="text"
                          className="novaos-input with-icon"
                          value={formTarefa.endereco_atendimento}
                          onChange={e => setFieldTarefa('endereco_atendimento', e.target.value)}
                          placeholder="Ex: Borracharia do Zé, Av. Brasil 1200…"
                        />
                      </div>
                    </div>

                    {/* Responsável */}
                    <div className="novaos-input-group">
                      <label className="novaos-label">
                        Responsável
                        <span className="badge-auto">(opcional)</span>
                      </label>

                      <div className="novaos-input-icon">
                        <FaHardHat className="input-icon" />

                        <select
                          className="novaos-select with-icon"
                          value={formTarefa.motorista_id}
                          onChange={e => setFieldTarefa('motorista_id', e.target.value)}
                        >
                          <option value="">Nenhum responsável definido</option>
                          {motoristas.map(m => (
                            <option key={m.id} value={String(m.id)}>
                              {m.nome}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    {/* Data e Hora */}
                    <div className="novaos-input-group">
                      <label className="novaos-label">
                        Data e Hora <span className="req">*</span>
                      </label>

                      <div className="novaos-input-icon">
                        <FaCalendarAlt className="input-icon" />

                        <input
                          type="datetime-local"
                          className="novaos-input with-icon"
                          value={formTarefa.data_hora}
                          onChange={e => setFieldTarefa('data_hora', e.target.value)}
                          required
                        />
                      </div>
                    </div>
                  </div>

                  {/* ──────── DESCRIÇÃO & CUSTO ─────────────────────────── */}
                  <div className="novaos-card">
                    <div className="novaos-card-title">
                      <FaClipboardList className="icon-blue" />
                      Descrição &amp; Custo
                    </div>

                    {/* Descrição */}
                    <div className="novaos-input-group">
                      <label className="novaos-label">
                        Descrição da Tarefa <span className="req">*</span>
                      </label>

                      <textarea
                        className="novaos-textarea"
                        rows={4}
                        value={formTarefa.descricao}
                        onChange={e => setFieldTarefa('descricao', e.target.value)}
                        placeholder="Ex: Levar o veículo para troca dos 4 pneus na borracharia parceira…"
                        required
                      />
                    </div>

                    {/* Custo estimado — oculto por padrão */}
                    <div className="novaos-input-group">
                      {!mostrarCusto ? (
                        <button
                          type="button"
                          className="novaos-custo-toggle-btn"
                          onClick={() => setMostrarCusto(true)}
                        >
                          <FaCoins size={12} /> Adicionar custo estimado
                        </button>
                      ) : (
                        <>
                          <label className="novaos-label">Custo Estimado (R$)</label>

                          {/* [SEM-VALOR] Tarefa sem custo → campo cinza e valor 0. */}
                          <label
                            style={{
                              display:'flex', alignItems:'center', gap:8,
                              margin:'2px 0 12px', fontSize:'0.8rem',
                              fontWeight:600, color:'#475569', cursor:'pointer',
                              userSelect:'none',
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={semValor}
                              onChange={e => {
                                const marcado = e.target.checked;
                                setSemValor(marcado);
                                if (marcado) setFieldTarefa('custo', '');
                              }}
                              style={{ width:16, height:16, cursor:'pointer' }}
                            />
                            <span>OS sem valor (tarefa sem custo)</span>
                          </label>

                          <div className="novaos-valor-row">
                            <div
                              className="novaos-input-icon"
                              style={{ flex:1, opacity: semValor ? 0.55 : 1 }}
                            >
                              <FaDollarSign className="input-icon" />

                              <input
                                type="number"
                                min="0"
                                step="0.01"
                                className="novaos-input with-icon"
                                value={semValor ? '' : formTarefa.custo}
                                onChange={e => setFieldTarefa('custo', e.target.value)}
                                placeholder={semValor ? 'Sem valor' : '0.00'}
                                disabled={semValor}
                                autoFocus={!semValor}
                              />
                            </div>

                            <button
                              type="button"
                              className="novaos-custo-remove-btn"
                              onClick={() => {
                                setMostrarCusto(false);
                                setSemValor(false);
                                setFieldTarefa('custo', '');
                              }}
                              title="Remover custo estimado"
                            >
                              <FaTimes size={12} />
                            </button>
                          </div>

                          <p className="novaos-custo-hint">
                            {semValor
                              ? 'Esta tarefa será registrada sem custo — não gera despesa nem conta a pagar quando concluída.'
                              : 'Esse valor será lançado como despesa pendente em Contas a Pagar quando a tarefa for concluída pelo App.'}
                          </p>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                {/* ════ FOOTER — AÇÕES ════════════════════════════════════ */}
                <div className="novaos-footer">
                  <button type="button" className="novaos-btn-cancel" onClick={onClose}>
                    Cancelar
                  </button>

                  <button
                    type="submit"
                    className="novaos-btn-confirm novaos-btn-confirm--tarefa"
                    disabled={loading || submitted}
                  >
                    {loading ? (
                      <>
                        <FaSpinner style={{ animation:'spin 1s linear infinite' }} />
                        Salvando…
                      </>
                    ) : submitted ? (
                      <>
                        <FaCheckCircle />
                        Tarefa Criada!
                      </>
                    ) : (
                      'Registrar Tarefa'
                    )}
                  </button>
                </div>
              </form>
            )
          )}
        </div>
      </div>
    </>
  );
}